import * as React from 'react';

import { css, StyleSheet } from 'aphrodite';
import { Col, Row } from 'reactstrap';
import { isIE } from 'react-device-detect';
import newtheme from '../../newtheme';
import UseCaseOutcomeDesktop from '../../assets/images/UseCaseOutcomeDesktop';
import UseCaseTimeCommitmentDesktop from '../../assets/images/UseCaseTimeCommitmentDesktop';
import { MpTrackingButtons } from '../../config/buttons';

export interface ISingleUseCase {
    label: string;
    value: {
        trackingButton: MpTrackingButtons;
        image: {
            url: string;
            backgroundPosition: string;
        };
        time: {
            duration: string;
            unit: string;
            activity: string;
        };
        outcome: {
            value: string;
            text: string;
        };
    };
}

interface IProps {
    useCase: ISingleUseCase;
}

class SingleUseCase extends React.Component<IProps> {
    public render() {
        const { useCase } = this.props;

        return (
            <Row className={css(styles.useCaseWrapper)}>
                <Col xs={12} md={4}>
                    <div
                        className={css(styles.imageContainer, styles.topImageContainer)}
                        style={{ backgroundImage: `url(${useCase.value.image.url})`, backgroundPosition: useCase.value.image.backgroundPosition }}
                    />
                </Col>
                <Col xs={6} md={4}>
                    <div className={css(styles.imageContainer)}>
                        <UseCaseTimeCommitmentDesktop className={css(styles.useCaseOutcomeImage)} />
                        <div className={css(styles.duration)}>
                            <p key={useCase.value.time.duration} className={css(styles.durationTime)}>
                                {useCase.value.time.duration}
                            </p>
                            <p key={useCase.value.time.unit} className={css(styles.durationUnit)}>
                                {useCase.value.time.unit}
                            </p>
                        </div>
                        <p key={useCase.value.time.activity} className={css(styles.durationActivity)}>
                            {useCase.value.time.activity}
                        </p>
                    </div>
                </Col>
                <Col xs={6} md={4}>
                    <div className={css(styles.imageContainer)}>
                        <UseCaseOutcomeDesktop className={css(styles.useCaseOutcomeImage)} />
                        <p key={useCase.value.outcome.value} className={css(styles.outcomeValue, useCase.value.outcome.value.length > 1 && styles.outcomeValueSmall)}>
                            {useCase.value.outcome.value}
                        </p>
                        <p key={useCase.value.outcome.text} className={css(styles.outcomeText)}>
                            {useCase.value.outcome.text}
                        </p>
                    </div>
                </Col>
            </Row>
        );
    }
}

const opacityKeyframes = {
    from: {
        opacity: 0,
    },

    to: {
        opacity: 1,
    },
};

const styles = StyleSheet.create({
    useCaseWrapper: {
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            width: 730,
            margin: '0 auto',
        },
    },
    imageContainer: {
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            top: 77,
            backgroundPosition: 'top',
            borderRadius: 15,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            top: 80,
        },
        width: '100%',
        paddingTop: '118%',
        '-webkit-transition': 'background 0.7s ease',
        '-moz-transition': 'background 0.7s ease',
        '-o-transition': 'background 0.7s ease',
        transition: 'background 0.7s ease',
        '-webkit-backface-visibility': 'hidden',
        '-webkit-transform': 'translate3d(0,0,0)',
        '-webkit-transform-style': 'preserve-3d',
        backgroundSize: 'cover',
        // backgroundPosition: "80% 0%",
        borderRadius: 30,
        color: 'white',
    },
    topImageContainer: {
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            paddingTop: '45%',
            marginBottom: 30,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            paddingTop: '55%',
        },
    },
    useCaseOutcomeImage: {
        width: '100%',
        position: 'absolute',
        top: 0,
    },
    duration: {
        [newtheme.cssBreakpointsWithoutHeight.large]: {
            top: 95,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            top: 70,
        },
        [newtheme.cssBreakpointsWithoutHeight.medium]: {
            top: 70,
        },
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            top: 77,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            top: '25%',
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'absolute',
        top: 110,
        left: '50%',
        transform: isIE ? 'translateX(-25%)' : 'translateX(-50%)',
    },
    durationTime: {
        [newtheme.cssBreakpointsWithoutHeight.large]: {
            fontSize: 60,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            fontSize: 40,
        },
        [newtheme.cssBreakpointsWithoutHeight.medium]: {
            fontSize: 40,
        },
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            fontSize: 50,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            fontSize: '10vw',
        },
        position: 'relative',
        left: 0,
        fontSize: 80,
        fontWeight: 400,
        margin: 0,
        animationName: [opacityKeyframes],
        animationDuration: '1.5s',
    },
    durationUnit: {
        [newtheme.cssBreakpointsWithoutHeight.large]: {
            fontSize: 15,
            marginTop: -20,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            fontSize: 14,
            marginTop: -13,
        },
        [newtheme.cssBreakpointsWithoutHeight.medium]: {
            fontSize: 14,
            marginTop: -13,
        },
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            fontSize: 13,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            fontSize: '3vw',
        },
        fontSize: 16,
        fontWeight: 400,
        margin: 0,
        marginTop: -23,
        animationName: [opacityKeyframes],
        animationDuration: '1.5s',
    },
    durationActivity: {
        [newtheme.cssBreakpoints.large]: {
            bottom: 10,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            bottom: 0,
            fontSize: 17,
        },
        [newtheme.cssBreakpointsWithoutHeight.medium]: {
            bottom: 0,
            fontSize: 17,
        },
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            bottom: 7,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            fontSize: '3vw',
        },
        position: 'absolute',
        left: '50%',
        transform: isIE ? 'translateX(-35%)' : 'translateX(-50%)',
        bottom: 20,
        textTransform: 'uppercase',
        fontSize: 24,
        letterSpacing: '0.38em',
        textAlign: 'center',
        animationName: [opacityKeyframes],
        animationDuration: '1.5s',
    },
    outcomeValue: {
        [newtheme.cssBreakpointsWithoutHeight.large]: {
            fontSize: 120,
            top: 20,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            fontSize: 90,
            top: 12,
        },
        [newtheme.cssBreakpointsWithoutHeight.medium]: {
            fontSize: 90,
            top: 12,
        },
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            fontSize: 100,
            top: 15,
            left: isIE ? 43 : 17,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            fontSize: '15vw',
            top: '13%',
        },
        fontSize: 160,
        fontWeight: 500,
        position: 'absolute',
        top: 12,
        left: isIE ? 43 : 25,
        animationName: [opacityKeyframes],
        animationDuration: '1.5s',
    },
    outcomeValueSmall: {
        [newtheme.cssBreakpointsWithoutHeight.large]: {
            fontSize: 80,
            top: 60,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            fontSize: 50,
            top: 55,
        },
        [newtheme.cssBreakpointsWithoutHeight.medium]: {
            fontSize: 50,
            top: 55,
        },
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            fontSize: 60,
            top: 58,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            fontSize: '11vw',
            top: '21%',
        },
        fontSize: 110,
        top: 64,
        animationName: [opacityKeyframes],
        animationDuration: '1.5s',
    },
    outcomeText: {
        [newtheme.cssBreakpointsWithoutHeight.large]: {
            fontSize: 18,
            top: 170,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            fontSize: 14,
            top: 120,
        },
        [newtheme.cssBreakpointsWithoutHeight.medium]: {
            fontSize: 14,
            top: 120,
            paddingLeft: isIE ? 30 : 20,
        },
        [newtheme.cssBreakpointsWithoutHeight.small]: {
            fontSize: 14,
            top: 140,
        },
        [newtheme.cssBreakpointsWithoutHeight.tiny]: {
            fontSize: '3vw',
            top: '50%',
            paddingRight: 30,
            paddingLeft: 20,
        },
        '@media (max-width: 375px)': {
            fontSize: '2.7vw',
        },
        position: 'absolute',
        fontSize: 20,
        top: 200,
        paddingLeft: 30,
        paddingRight: 40,
        animationName: [opacityKeyframes],
        animationDuration: '1.5s',
    },
});

export default SingleUseCase;
